import { Vec2 } from 'ayvri-std/lib/vector';
import { defaultValue, defined } from './helpers';
export const EventType = {
    /**
     * Represents a mouse left button down event.
     *
     * @type {Number}
     * @constant
     */
    LEFT_DOWN: 0,
    /**
     * Represents a mouse left button up event.
     *
     * @type {Number}
     * @constant
     */
    LEFT_UP: 1,
    /**
     * Represents a mouse left click event.
     *
     * @type {Number}
     * @constant
     */
    LEFT_CLICK: 2,
    /**
     * Represents a mouse left double click event.
     *
     * @type {Number}
     * @constant
     */
    LEFT_DOUBLE_CLICK: 3,
    /**
     * Represents a mouse left button down event.
     *
     * @type {Number}
     * @constant
     */
    RIGHT_DOWN: 5,
    /**
     * Represents a mouse right button up event.
     *
     * @type {Number}
     * @constant
     */
    RIGHT_UP: 6,
    /**
     * Represents a mouse right click event.
     *
     * @type {Number}
     * @constant
     */
    RIGHT_CLICK: 7,
    /**
     * Represents a mouse middle button down event.
     *
     * @type {Number}
     * @constant
     */
    MIDDLE_DOWN: 10,
    /**
     * Represents a mouse middle button up event.
     *
     * @type {Number}
     * @constant
     */
    MIDDLE_UP: 11,
    /**
     * Represents a mouse middle click event.
     *
     * @type {Number}
     * @constant
     */
    MIDDLE_CLICK: 12,
    /**
     * Represents a mouse move event.
     *
     * @type {Number}
     * @constant
     */
    MOUSE_MOVE: 15,
    /**
     * Represents a mouse wheel event.
     *
     * @type {Number}
     * @constant
     */
    WHEEL: 16,
    /**
     * Represents the start of a two-finger event on a touch surface.
     *
     * @type {Number}
     * @constant
     */
    PINCH_START: 17,
    /**
     * Represents the end of a two-finger event on a touch surface.
     *
     * @type {Number}
     * @constant
     */
    PINCH_END: 18,
    /**
     * Represents a change of a two-finger event on a touch surface.
     *
     * @type {Number}
     * @constant
     */
    PINCH_MOVE: 19,
};
export const KeyboardEventModifier = {
    SHIFT: 0,
    CTRL: 1,
    ALT: 2,
};
/**
 * The amount of time, in milliseconds, that mouse events will be disabled after
 * receiving any touch events, such that any emulated mouse events will be ignored.
 * @type {Number}
 * @default 800
 */
const kMouseEmulationIgnoreMilliseconds = 800;
class AssociativeArray {
    constructor() {
        this._array = [];
        this._hash = {};
    }
    get length() {
        return this._array.length;
    }
    get values() {
        return this._array;
    }
    contains(key) {
        return defined(this._hash[key]);
    }
    set(key, value) {
        const oldValue = this._hash[key];
        if (value !== oldValue) {
            this.remove(key);
            this._hash[key] = value;
            this._array.push(value);
        }
    }
    get(key) {
        return this._hash[key];
    }
    remove(key) {
        const value = this._hash[key];
        const hasValue = defined(value);
        if (hasValue) {
            const array = this._array;
            array.splice(array.indexOf(value), 1);
            delete this._hash[key];
        }
        return hasValue;
    }
    removeAll() {
        const array = this._array;
        if (array.length > 0) {
            this._hash = {};
            array.length = 0;
        }
    }
}
/**
 * Gets a timestamp that can be used in measuring the time between events.  Timestamps
 * are expressed in milliseconds, but it is not specified what the milliseconds are
 * measured from.  This function uses performance.now() if it is available, or Date.now()
 * otherwise.
 */
let getTimestamp;
if (typeof performance !== 'undefined' && typeof performance.now === 'function' && Number.isFinite(performance.now())) {
    getTimestamp = function () {
        return performance.now();
    };
}
else {
    getTimestamp = function () {
        return Date.now();
    };
}
function supportsPointerEvents() {
    // While navigator.pointerEnabled is deprecated in the W3C specification
    // we still need to use it if it exists in order to support browsers
    // that rely on it, such as the Windows WebBrowser control which defines
    // PointerEvent but sets navigator.pointerEnabled to false.
    return typeof PointerEvent !== 'undefined' || defaultValue(navigator, {}).pointerEnabled;
}
function getPosition(screenSpaceEventHandler, event, result) {
    const element = screenSpaceEventHandler._element;
    if (element === document) {
        result.x = event.clientX;
        result.y = event.clientY;
        return result;
    }
    const rect = element.getBoundingClientRect();
    result.x = event.clientX - rect.left;
    result.y = event.clientY - rect.top;
    return result;
}
function getInputEventKey(type, modifier) {
    let key = type;
    if (defined(modifier)) {
        key += `+${modifier}`;
    }
    return key;
}
function getModifier(event) {
    if (event.shiftKey) {
        return KeyboardEventModifier.SHIFT;
    }
    else if (event.ctrlKey) {
        return KeyboardEventModifier.CTRL;
    }
    else if (event.altKey) {
        return KeyboardEventModifier.ALT;
    }
    return undefined;
}
const MouseButton = {
    LEFT: 0,
    MIDDLE: 1,
    RIGHT: 2,
};
function registerListener(screenSpaceEventHandler, domType, element, callback) {
    function listener(e) {
        callback(screenSpaceEventHandler, e);
    }
    element.addEventListener(domType, listener, false);
    screenSpaceEventHandler._removalFunctions.push(() => {
        element.removeEventListener(domType, listener, false);
    });
}
function registerListeners(screenSpaceEventHandler) {
    const element = screenSpaceEventHandler._element;
    // some listeners may be registered on the document, so we still get events even after
    // leaving the bounds of element.
    // this is affected by the existence of an undocumented disableRootEvents property on element.
    const alternateElement = !defined(element.disableRootEvents) ? document : element;
    if (supportsPointerEvents()) {
        registerListener(screenSpaceEventHandler, 'pointerdown', element, handlePointerDown);
        registerListener(screenSpaceEventHandler, 'pointerup', element, handlePointerUp);
        registerListener(screenSpaceEventHandler, 'pointermove', element, handlePointerMove);
        registerListener(screenSpaceEventHandler, 'pointercancel', element, handlePointerUp);
    }
    else {
        registerListener(screenSpaceEventHandler, 'mousedown', element, handleMouseDown);
        registerListener(screenSpaceEventHandler, 'mouseup', alternateElement, handleMouseUp);
        registerListener(screenSpaceEventHandler, 'mousemove', alternateElement, handleMouseMove);
        registerListener(screenSpaceEventHandler, 'touchstart', element, handleTouchStart);
        registerListener(screenSpaceEventHandler, 'touchend', alternateElement, handleTouchEnd);
        registerListener(screenSpaceEventHandler, 'touchmove', alternateElement, handleTouchMove);
        registerListener(screenSpaceEventHandler, 'touchcancel', alternateElement, handleTouchEnd);
    }
    registerListener(screenSpaceEventHandler, 'dblclick', element, handleDblClick);
    // detect available wheel event
    let wheelEvent;
    if ('onwheel' in element) {
        // spec event type
        wheelEvent = 'wheel';
    }
    else if (document.onmousewheel !== undefined) {
        // legacy event type
        wheelEvent = 'mousewheel';
    }
    else {
        // older Firefox
        wheelEvent = 'DOMMouseScroll';
    }
    registerListener(screenSpaceEventHandler, wheelEvent, element, handleWheel);
}
const mouseDownEvent = {
    position: new Vec2(),
};
function gotTouchEvent(screenSpaceEventHandler) {
    screenSpaceEventHandler._lastSeenTouchEvent = getTimestamp();
}
function canProcessMouseEvent(screenSpaceEventHandler) {
    return getTimestamp() - screenSpaceEventHandler._lastSeenTouchEvent > kMouseEmulationIgnoreMilliseconds;
}
function handleMouseDown(screenSpaceEventHandler, event) {
    if (!canProcessMouseEvent(screenSpaceEventHandler)) {
        return;
    }
    const button = event.button;
    screenSpaceEventHandler._buttonDown = button;
    let screenSpaceEventType;
    if (button === MouseButton.LEFT) {
        screenSpaceEventType = EventType.LEFT_DOWN;
    }
    else if (button === MouseButton.MIDDLE) {
        screenSpaceEventType = EventType.MIDDLE_DOWN;
    }
    else if (button === MouseButton.RIGHT) {
        screenSpaceEventType = EventType.RIGHT_DOWN;
    }
    else {
        return;
    }
    const position = getPosition(screenSpaceEventHandler, event, screenSpaceEventHandler._primaryPosition);
    screenSpaceEventHandler._primaryStartPosition.copy(position);
    screenSpaceEventHandler._primaryPreviousPosition.copy(position);
    const modifier = getModifier(event);
    const action = screenSpaceEventHandler.getInputAction(screenSpaceEventType, modifier);
    if (defined(action)) {
        mouseDownEvent.position.copy(position);
        action(mouseDownEvent);
        event.preventDefault();
    }
}
const mouseUpEvent = {
    position: new Vec2(),
};
const mouseClickEvent = {
    position: new Vec2(),
};
function handleMouseUp(screenSpaceEventHandler, event) {
    if (!canProcessMouseEvent(screenSpaceEventHandler)) {
        return;
    }
    const button = event.button;
    screenSpaceEventHandler._buttonDown = undefined;
    let screenSpaceEventType;
    let clickScreenSpaceEventType;
    if (button === MouseButton.LEFT) {
        screenSpaceEventType = EventType.LEFT_UP;
        clickScreenSpaceEventType = EventType.LEFT_CLICK;
    }
    else if (button === MouseButton.MIDDLE) {
        screenSpaceEventType = EventType.MIDDLE_UP;
        clickScreenSpaceEventType = EventType.MIDDLE_CLICK;
    }
    else if (button === MouseButton.RIGHT) {
        screenSpaceEventType = EventType.RIGHT_UP;
        clickScreenSpaceEventType = EventType.RIGHT_CLICK;
    }
    else {
        return;
    }
    const modifier = getModifier(event);
    const action = screenSpaceEventHandler.getInputAction(screenSpaceEventType, modifier);
    const clickAction = screenSpaceEventHandler.getInputAction(clickScreenSpaceEventType, modifier);
    if (defined(action) || defined(clickAction)) {
        const position = getPosition(screenSpaceEventHandler, event, screenSpaceEventHandler._primaryPosition);
        if (defined(action)) {
            mouseUpEvent.position.copy(position);
            action(mouseUpEvent);
        }
        if (defined(clickAction)) {
            const startPosition = screenSpaceEventHandler._primaryStartPosition;
            const xDiff = startPosition.x - position.x;
            const yDiff = startPosition.y - position.y;
            const totalPixels = Math.sqrt(xDiff * xDiff + yDiff * yDiff);
            if (totalPixels < screenSpaceEventHandler._clickPixelTolerance) {
                mouseClickEvent.position.copy(position);
                clickAction(mouseClickEvent);
            }
        }
    }
}
const mouseMoveEvent = {
    startPosition: new Vec2(),
    endPosition: new Vec2(),
};
function handleMouseMove(screenSpaceEventHandler, event) {
    if (!canProcessMouseEvent(screenSpaceEventHandler)) {
        return;
    }
    const modifier = getModifier(event);
    const position = getPosition(screenSpaceEventHandler, event, screenSpaceEventHandler._primaryPosition);
    const previousPosition = screenSpaceEventHandler._primaryPreviousPosition;
    const action = screenSpaceEventHandler.getInputAction(EventType.MOUSE_MOVE, modifier);
    if (defined(action)) {
        mouseMoveEvent.startPosition.copy(previousPosition);
        mouseMoveEvent.endPosition.copy(position);
        action(mouseMoveEvent);
    }
    previousPosition.copy(position);
    if (defined(screenSpaceEventHandler._buttonDown)) {
        event.preventDefault();
    }
}
const mouseDblClickEvent = {
    position: new Vec2(),
};
function handleDblClick(screenSpaceEventHandler, event) {
    const button = event.button;
    let screenSpaceEventType;
    if (button === MouseButton.LEFT) {
        screenSpaceEventType = EventType.LEFT_DOUBLE_CLICK;
    }
    else {
        return;
    }
    const modifier = getModifier(event);
    const action = screenSpaceEventHandler.getInputAction(screenSpaceEventType, modifier);
    if (defined(action)) {
        getPosition(screenSpaceEventHandler, event, mouseDblClickEvent.position);
        action(mouseDblClickEvent);
    }
}
function handleWheel(screenSpaceEventHandler, event) {
    // currently this event exposes the delta value in terms of
    // the obsolete mousewheel event type.  so, for now, we adapt the other
    // values to that scheme.
    let delta;
    // standard wheel event uses deltaY.  sign is opposite wheelDelta.
    // deltaMode indicates what unit it is in.
    if (defined(event.deltaY)) {
        const deltaMode = event.deltaMode;
        if (deltaMode === event.DOM_DELTA_PIXEL) {
            delta = -event.deltaY;
        }
        else if (deltaMode === event.DOM_DELTA_LINE) {
            delta = -event.deltaY * 40;
        }
        else {
            // DOM_DELTA_PAGE
            delta = -event.deltaY * 120;
        }
    }
    else if (event.detail > 0) {
        // old Firefox versions use event.detail to count the number of clicks. The sign
        // of the integer is the direction the wheel is scrolled.
        delta = event.detail * -120;
    }
    else {
        delta = event.wheelDelta;
    }
    if (!defined(delta)) {
        return;
    }
    const modifier = getModifier(event);
    const action = screenSpaceEventHandler.getInputAction(EventType.WHEEL, modifier);
    if (defined(action)) {
        action(delta);
        event.preventDefault();
    }
}
function handleTouchStart(screenSpaceEventHandler, event) {
    gotTouchEvent(screenSpaceEventHandler);
    const changedTouches = event.changedTouches;
    let i;
    const length = changedTouches.length;
    let touch;
    let identifier;
    const positions = screenSpaceEventHandler._positions;
    for (i = 0; i < length; ++i) {
        touch = changedTouches[i];
        identifier = touch.identifier;
        positions.set(identifier, getPosition(screenSpaceEventHandler, touch, new Vec2()));
    }
    fireTouchEvents(screenSpaceEventHandler, event);
    const previousPositions = screenSpaceEventHandler._previousPositions;
    for (i = 0; i < length; ++i) {
        touch = changedTouches[i];
        identifier = touch.identifier;
        previousPositions.set(identifier, positions.get(identifier).clone());
    }
}
function handleTouchEnd(screenSpaceEventHandler, event) {
    gotTouchEvent(screenSpaceEventHandler);
    const changedTouches = event.changedTouches;
    let i;
    const length = changedTouches.length;
    let touch;
    let identifier;
    const positions = screenSpaceEventHandler._positions;
    for (i = 0; i < length; ++i) {
        touch = changedTouches[i];
        identifier = touch.identifier;
        positions.remove(identifier);
    }
    fireTouchEvents(screenSpaceEventHandler, event);
    const previousPositions = screenSpaceEventHandler._previousPositions;
    for (i = 0; i < length; ++i) {
        touch = changedTouches[i];
        identifier = touch.identifier;
        previousPositions.remove(identifier);
    }
}
const touchStartEvent = {
    position: new Vec2(),
};
const touch2StartEvent = {
    position1: new Vec2(),
    position2: new Vec2(),
};
const touchEndEvent = {
    position: new Vec2(),
};
const touchClickEvent = {
    position: new Vec2(),
};
function fireTouchEvents(screenSpaceEventHandler, event) {
    const modifier = getModifier(event);
    const positions = screenSpaceEventHandler._positions;
    const previousPositions = screenSpaceEventHandler._previousPositions;
    const numberOfTouches = positions.length;
    let action;
    let clickAction;
    const pinching = screenSpaceEventHandler._isPinching;
    if (numberOfTouches !== 1 && screenSpaceEventHandler._buttonDown === MouseButton.LEFT) {
        // transitioning from single touch, trigger UP and might trigger CLICK
        screenSpaceEventHandler._buttonDown = undefined;
        action = screenSpaceEventHandler.getInputAction(EventType.LEFT_UP, modifier);
        if (defined(action)) {
            touchEndEvent.position.copy(screenSpaceEventHandler._primaryPosition);
            action(touchEndEvent);
        }
        if (numberOfTouches === 0) {
            // releasing single touch, check for CLICK
            clickAction = screenSpaceEventHandler.getInputAction(EventType.LEFT_CLICK, modifier);
            if (defined(clickAction)) {
                const startPosition = screenSpaceEventHandler._primaryStartPosition;
                const endPosition = previousPositions.values[0];
                const xDiff = startPosition.x - endPosition.x;
                const yDiff = startPosition.y - endPosition.y;
                const totalPixels = Math.sqrt(xDiff * xDiff + yDiff * yDiff);
                if (totalPixels < screenSpaceEventHandler._clickPixelTolerance) {
                    touchClickEvent.position.copy(screenSpaceEventHandler._primaryPosition);
                    clickAction(touchClickEvent);
                }
            }
        }
        // Otherwise don't trigger CLICK, because we are adding more touches.
    }
    if (numberOfTouches === 0 && pinching) {
        // transitioning from pinch, trigger PINCH_END
        screenSpaceEventHandler._isPinching = false;
        action = screenSpaceEventHandler.getInputAction(EventType.PINCH_END, modifier);
        if (defined(action)) {
            action();
        }
    }
    if (numberOfTouches === 1 && !pinching) {
        // transitioning to single touch, trigger DOWN
        const position = positions.values[0];
        screenSpaceEventHandler._primaryPosition.copy(position);
        screenSpaceEventHandler._primaryStartPosition.copy(position);
        screenSpaceEventHandler._primaryPreviousPosition.copy(position);
        screenSpaceEventHandler._buttonDown = MouseButton.LEFT;
        action = screenSpaceEventHandler.getInputAction(EventType.LEFT_DOWN, modifier);
        if (defined(action)) {
            touchStartEvent.position.copy(position);
            action(touchStartEvent);
        }
        event.preventDefault();
    }
    if (numberOfTouches === 2 && !pinching) {
        // transitioning to pinch, trigger PINCH_START
        screenSpaceEventHandler._isPinching = true;
        action = screenSpaceEventHandler.getInputAction(EventType.PINCH_START, modifier);
        if (defined(action)) {
            touch2StartEvent.position1.copy(positions.values[0]);
            touch2StartEvent.position2.copy(positions.values[1]);
            action(touch2StartEvent);
            // Touch-enabled devices, in particular iOS can have many default behaviours for
            // "pinch" events, which can still be executed unless we prevent them here.
            event.preventDefault();
        }
    }
}
function handleTouchMove(screenSpaceEventHandler, event) {
    gotTouchEvent(screenSpaceEventHandler);
    const changedTouches = event.changedTouches;
    let i;
    const length = changedTouches.length;
    let touch;
    let previousPosition;
    let identifier;
    const positions = screenSpaceEventHandler._positions;
    for (i = 0; i < length; ++i) {
        touch = changedTouches[i];
        identifier = touch.identifier;
        const position = positions.get(identifier);
        if (defined(position)) {
            getPosition(screenSpaceEventHandler, touch, position);
        }
    }
    fireTouchMoveEvents(screenSpaceEventHandler, event);
    const previousPositions = screenSpaceEventHandler._previousPositions;
    for (i = 0; i < length; ++i) {
        touch = changedTouches[i];
        identifier = touch.identifier;
        previousPosition = previousPositions.get(identifier);
        if (previousPosition)
            previousPosition.copy(positions.get(identifier));
    }
}
const touchMoveEvent = {
    startPosition: new Vec2(),
    endPosition: new Vec2(),
};
const touchPinchMovementEvent = {
    distance: {
        startPosition: new Vec2(),
        endPosition: new Vec2(),
    },
    angleAndHeight: {
        startPosition: new Vec2(),
        endPosition: new Vec2(),
    },
};
function fireTouchMoveEvents(screenSpaceEventHandler, event) {
    const modifier = getModifier(event);
    const positions = screenSpaceEventHandler._positions;
    const previousPositions = screenSpaceEventHandler._previousPositions;
    const numberOfTouches = positions.length;
    let action;
    if (numberOfTouches === 1 && screenSpaceEventHandler._buttonDown === MouseButton.LEFT) {
        // moving single touch
        const position = positions.values[0];
        screenSpaceEventHandler._primaryPosition.copy(position);
        const previousPosition = screenSpaceEventHandler._primaryPreviousPosition;
        action = screenSpaceEventHandler.getInputAction(EventType.MOUSE_MOVE, modifier);
        if (defined(action)) {
            touchMoveEvent.startPosition.copy(previousPosition);
            touchMoveEvent.endPosition.copy(position);
            action(touchMoveEvent);
        }
        previousPosition.copy(position);
        event.preventDefault();
    }
    else if (numberOfTouches === 2 && screenSpaceEventHandler._isPinching) {
        // moving pinch
        action = screenSpaceEventHandler.getInputAction(EventType.PINCH_MOVE, modifier);
        if (defined(action)) {
            const position1 = positions.values[0];
            const position2 = positions.values[1];
            const previousPosition1 = previousPositions.values[0];
            const previousPosition2 = previousPositions.values[1];
            const dX = position2.x - position1.x;
            const dY = position2.y - position1.y;
            const dist = Math.sqrt(dX * dX + dY * dY) * 0.25;
            const prevDX = previousPosition2.x - previousPosition1.x;
            const prevDY = previousPosition2.y - previousPosition1.y;
            const prevDist = Math.sqrt(prevDX * prevDX + prevDY * prevDY) * 0.25;
            const cY = (position2.y + position1.y) * 0.125;
            const prevCY = (previousPosition2.y + previousPosition1.y) * 0.125;
            const angle = Math.atan2(dY, dX);
            const prevAngle = Math.atan2(prevDY, prevDX);
            touchPinchMovementEvent.distance.startPosition.init(0.0, prevDist);
            touchPinchMovementEvent.distance.endPosition.init(0.0, dist);
            touchPinchMovementEvent.angleAndHeight.startPosition.init(prevAngle, prevCY);
            touchPinchMovementEvent.angleAndHeight.endPosition.init(angle, cY);
            action(touchPinchMovementEvent);
        }
    }
}
function handlePointerDown(screenSpaceEventHandler, event) {
    event.target.setPointerCapture(event.pointerId);
    if (event.pointerType === 'touch') {
        const positions = screenSpaceEventHandler._positions;
        const identifier = event.pointerId;
        positions.set(identifier, getPosition(screenSpaceEventHandler, event, new Vec2()));
        fireTouchEvents(screenSpaceEventHandler, event);
        const previousPositions = screenSpaceEventHandler._previousPositions;
        previousPositions.set(identifier, positions.get(identifier).clone());
    }
    else {
        handleMouseDown(screenSpaceEventHandler, event);
    }
}
function handlePointerUp(screenSpaceEventHandler, event) {
    if (event.pointerType === 'touch') {
        const positions = screenSpaceEventHandler._positions;
        const identifier = event.pointerId;
        positions.remove(identifier);
        fireTouchEvents(screenSpaceEventHandler, event);
        const previousPositions = screenSpaceEventHandler._previousPositions;
        previousPositions.remove(identifier);
    }
    else {
        handleMouseUp(screenSpaceEventHandler, event);
    }
}
function handlePointerMove(screenSpaceEventHandler, event) {
    if (event.pointerType === 'touch') {
        const positions = screenSpaceEventHandler._positions;
        const identifier = event.pointerId;
        const position = positions.get(identifier);
        if (!defined(position)) {
            return;
        }
        getPosition(screenSpaceEventHandler, event, position);
        fireTouchMoveEvents(screenSpaceEventHandler, event);
        const previousPositions = screenSpaceEventHandler._previousPositions;
        const previousPosition = previousPositions.get(identifier);
        if (previousPosition)
            previousPosition.copy(positions.get(identifier));
    }
    else {
        handleMouseMove(screenSpaceEventHandler, event);
    }
}
/**
 * Handles user input events. Custom functions can be added to be executed on
 * when the user enters input.
 */
export default class InputEventHandler {
    constructor(element) {
        this._inputEvents = {};
        this._buttonDown = undefined;
        this._isPinching = false;
        this._lastSeenTouchEvent = -kMouseEmulationIgnoreMilliseconds;
        this._primaryStartPosition = new Vec2();
        this._primaryPosition = new Vec2();
        this._primaryPreviousPosition = new Vec2();
        this._positions = new AssociativeArray();
        this._previousPositions = new AssociativeArray();
        this._removalFunctions = [];
        // TODO: Revisit when doing mobile development. May need to be configurable
        // or determined based on the platform?
        this._clickPixelTolerance = 5;
        this._element = defaultValue(element, document);
        registerListeners(this);
    }
    /**
     * Set a function to be executed on an input event.
     *
     * @param {Function} action Function to be executed when the input event occurs.
     * @param {Number} type The ScreenSpaceEventType of input event.
     * @param {Number} [modifier] A KeyboardEventModifier key that is held when a <code>type</code>
     * event occurs.
     *
     * @see ScreenSpaceEventHandler#getInputAction
     * @see ScreenSpaceEventHandler#removeInputAction
     */
    setInputAction(action, type, modifier) {
        const key = getInputEventKey(type, modifier);
        this._inputEvents[key] = action;
    }
    /**
     * Returns the function to be executed on an input event.
     *
     * @param {Number} type The ScreenSpaceEventType of input event.
     * @param {Number} [modifier] A KeyboardEventModifier key that is held when a <code>type</code>
     * event occurs.
     *
     * @see ScreenSpaceEventHandler#setInputAction
     * @see ScreenSpaceEventHandler#removeInputAction
     */
    getInputAction(type, modifier) {
        const key = getInputEventKey(type, modifier);
        return this._inputEvents[key];
    }
    /**
     * Removes the function to be executed on an input event.
     *
     * @param {Number} type The ScreenSpaceEventType of input event.
     * @param {Number} [modifier] A KeyboardEventModifier key that is held when a <code>type</code>
     * event occurs.
     *
     * @see ScreenSpaceEventHandler#getInputAction
     * @see ScreenSpaceEventHandler#setInputAction
     */
    removeInputAction(type, modifier) {
        const key = getInputEventKey(type, modifier);
        delete this._inputEvents[key];
    }
}
